import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { RolesSearchBar, RolesTable } from '@personnel/presentation/components/list';
import {
  PageLayout,
  PrimaryHeader,
  SecondaryHeader,
  PersonnelTabs,
  DeleteRoleDialog,
  RoleHasUsersDialog,
  Pagination,
  ErrorState,
} from '@personnel/presentation/components/shared';
import { useRoleList, useDeleteRole, useUrlSearchParam, usePagination } from '@personnel/presentation/hooks';
import type { Role } from '@personnel/domain/entities';

/**
 * Roles List Page
 *
 * Main page for displaying and managing restaurant roles
 * Route: /roles
 *
 * @returns {React.ReactElement} Roles list page component
 */
export const RolesListPage = (): React.ReactElement => {
  const { t } = useTranslation('roles');
  const { t: tPersonnel } = useTranslation('personnel');
  const history = useHistory();

  // ✅ Sync search query with URL query params using custom hook
  const { value: searchQuery, setValue: setSearchQuery } = useUrlSearchParam('search', '');

  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('roles');
  const [roleToDelete, setRoleToDelete] = useState<{ uuid: string; name: string; usersCount: number } | null>(null);

  // ✅ Fetch all roles (no pagination from backend)
  const { roles, isLoading, isError, error, refetch } = useRoleList();
  // ✅ Delete role mutation
  const { deleteRole, isLoading: isDeleting } = useDeleteRole();

  // ✅ Filter roles based on search (client-side)
  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    return roles.filter((role: Role) => role.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery, roles]);

  // ✅ Pagination (client-side)
  const { paginatedData: paginatedRoles, currentPage, totalPages, totalItems, goToPage, resetPage } = usePagination(
    filteredRoles,
  );

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    resetPage();
  };

  const handleDeleteRole = (roleUuid: string) => {
    const role = roles.find((r) => r.uuid === roleUuid);
    if (!role) return;

    // Open appropriate dialog based on users count
    setRoleToDelete({
      uuid: roleUuid,
      name: role.name,
      usersCount: role.usersCount,
    });
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole(roleToDelete.uuid);
      // Close dialog after successful deletion
      setRoleToDelete(null);
    } catch (error) {
      // Error handling is done by the hook (toast notification)
      // Keep dialog open so user can retry or cancel
      // eslint-disable-next-line no-console
      console.error('Error deleting role:', error);
    }
  };

  const handleTabChange = (tab: 'users' | 'roles') => {
    setActiveTab(tab);
    if (tab === 'users') {
      history.push('/employees');
    }
  };

  return (
    <PageLayout>
      {/* Primary Header: Section-level */}
      <PrimaryHeader description={tPersonnel('section.description')} title={tPersonnel('section.title')} />

      {/* Tabs Navigation */}
      <PersonnelTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Secondary Header: Page-level */}
      <SecondaryHeader description={t('list.description')} title={t('list.title')} />

      {/* Main Content */}
      <div className="gap-4 flex flex-col">
        {/* Search Bar */}
        <RolesSearchBar disabled={isLoading} onSearch={handleSearch} value={searchQuery} />

        {/* Error State */}
        {isError && <ErrorState context="roles" error={error} onRetry={refetch} />}

        {/* Table - only show when no error */}
        {!isError && (
          <>
            <RolesTable isLoading={isLoading} onDeleteRole={handleDeleteRole} roles={paginatedRoles} />

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                onPageChange={goToPage}
                showRowCount={false}
                totalItems={totalItems}
                totalPages={totalPages}
              />
            )}
          </>
        )}
      </div>

      {/* Role Has Users Dialog (blocking) - shown when role has users assigned */}
      <RoleHasUsersDialog
        onOpenChange={(open) => !open && setRoleToDelete(null)}
        open={!!roleToDelete && roleToDelete.usersCount > 0}
        usersCount={roleToDelete?.usersCount || 0}
      />

      {/* Delete Confirmation Dialog - shown when role has no users */}
      <DeleteRoleDialog
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onOpenChange={(open) => !open && setRoleToDelete(null)}
        open={!!roleToDelete && roleToDelete.usersCount === 0}
        roleName={roleToDelete?.name || ''}
      />
    </PageLayout>
  );
};
